import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { tasksService, type TaskApi } from '../../../services/tasksService';
import { projectsService } from '../../../services/projectsService';
import TaskModal from '../TaskModal/TaskModal';
import './ProfileStatisticsModal.css';

export type ProfileStatisticsMetric = 'projects' | 'active' | 'high_priority' | 'overdue';

const metrics = {
  projects: {
    title: 'Участие в проектах',
    empty: 'Нет активных проектов с участием сотрудника.',
  },
  active: {
    title: 'Задачи сейчас в работе',
    empty: 'У сотрудника нет активных задач.',
  },
  high_priority: {
    title: 'Задачи с высоким приоритетом',
    empty: 'Нет активных задач с высоким приоритетом.',
  },
  overdue: {
    title: 'Задачи с истекшим сроком',
    empty: 'Просроченных задач нет.',
  },
};

const statusLabel = (status: string) => ({ pending: 'Ожидает', in_progress: 'В работе', completed: 'Завершена', cancelled: 'Отменена' }[status] || status);

export default function ProfileStatisticsModal({ user, metric, onClose }: {
  user: { id: number; name: string };
  metric: ProfileStatisticsMetric;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const [openedTask, setOpenedTask] = useState<TaskApi | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const info = metrics[metric];
  const isProjects = metric === 'projects';

  const taskQuery = useQuery({
    queryKey: ['tasks', 'profile-statistics', user.id, metric, page],
    queryFn: () => tasksService.getTasks({ dashboard_for_user_id: user.id, profile_metric: metric === 'projects' ? undefined : metric, page, per_page: 10 }),
    enabled: !isProjects,
    staleTime: 0,
  });
  const projectQuery = useQuery({
    queryKey: ['projects', 'profile-statistics', user.id, page],
    queryFn: () => projectsService.getProjects({ participant_user_id: user.id, page, per_page: 10 }),
    enabled: isProjects,
    staleTime: 0,
  });
  const query = isProjects ? projectQuery : taskQuery;
  const total = query.data?.total ?? 0;
  const lastPage = query.data?.last_page || 1;

  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);

  useEffect(() => {
    if (openedTask) return;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
      if (event.key === 'Tab') {
        const buttons = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], [tabindex="0"]') || []);
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, openedTask]);

  return createPortal(<>
    <div className="profile-statistics-overlay" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }} style={openedTask ? { display: 'none' } : undefined}>
      <div className="profile-statistics-modal" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="profile-statistics-modal-header">
          <div><p className="profile-statistics-modal-user">{user.name}</p><h2 id={titleId}>{info.title}</h2></div>
          <button ref={closeRef} type="button" className="profile-statistics-modal-close" onClick={onClose} aria-label="Закрыть статистику">×</button>
        </header>
        <div className="profile-statistics-modal-body" aria-busy={query.isFetching}>
          {query.isPending ? <p className="profile-statistics-modal-message" role="status">Загрузка…</p> : query.isError ? (
            <div className="profile-statistics-modal-message" role="alert"><p>Не удалось загрузить список.</p><button type="button" onClick={() => query.refetch()}>Попробовать снова</button></div>
          ) : total === 0 ? <p className="profile-statistics-modal-message">{info.empty}</p> : (
            <>
              <p className="profile-statistics-modal-total">{isProjects ? 'Проектов' : 'Задач'}: <strong>{total.toLocaleString('ru-RU')}</strong></p>
              {isProjects ? <ul className="profile-statistics-modal-list">
                {projectQuery.data?.data.map((project) => <li key={project.id} className="profile-statistics-modal-item">
                  <h3>{project.name}</h3>
                  {project.description && <p className="profile-statistics-modal-item-description">{project.description}</p>}
                  <dl><div><dt>Клиент</dt><dd>{project.client || 'Не указан'}</dd></div><div><dt>Руководитель</dt><dd>{project.manager?.name || 'Не указан'}</dd></div><div><dt>Дела / задачи</dt><dd>{project.casesCount} / {project.tasksCount}</dd></div></dl>
                </li>)}
              </ul> : <ul className="profile-statistics-modal-list">
                {taskQuery.data?.data.map((task) => <li key={task.id} className="profile-statistics-modal-item">
                  <div className="profile-statistics-modal-item-heading"><h3><button type="button" onClick={() => setOpenedTask(task)}>{task.title}</button></h3><span className="profile-statistics-modal-status">{statusLabel(task.status)}</span></div>
                  {task.description && <p className="profile-statistics-modal-item-description">{task.description}</p>}
                  <dl><div><dt>Срок</dt><dd className={task.is_overdue ? 'profile-statistics-modal-overdue' : ''}>{task.deadline_formatted || 'Не указан'}</dd></div><div><dt>Постановщик</dt><dd>{task.assigner?.name || 'Не указан'}</dd></div><div><dt>Исполнители</dt><dd>{task.performers.map((performer) => performer.name).join(', ') || 'Не указаны'}</dd></div>
                    {task.client && <div><dt>Клиент</dt><dd>{task.client.name}</dd></div>}{task.project && <div><dt>Проект</dt><dd>{task.project.name}</dd></div>}{task.case && <div><dt>Дело</dt><dd>{task.case.name}</dd></div>}
                  </dl>
                </li>)}
              </ul>}
            </>
          )}
        </div>
        <footer className="profile-statistics-modal-footer">
          <span aria-live="polite">{!query.isPending && !query.isError && total > 0 ? `${(page - 1) * 10 + 1}–${Math.min(page * 10, total)} из ${total}` : ''}</span>
          <div className="profile-statistics-modal-pagination">
            <button type="button" aria-label="Предыдущая страница" disabled={page <= 1 || query.isFetching} onClick={() => setPage((value) => value - 1)}>←</button>
            <span>{page} / {lastPage}</span>
            <button type="button" aria-label="Следующая страница" disabled={page >= lastPage || query.isFetching} onClick={() => { setPage((value) => value + 1); dialogRef.current?.querySelector('.profile-statistics-modal-body')?.scrollTo?.(0, 0); }}>→</button>
          </div>
        </footer>
      </div>
    </div>
    {openedTask && <TaskModal isOpen task={{ id: openedTask.id, name: openedTask.title, assignmentDate: openedTask.created_at_formatted || '', dueDate: openedTask.deadline_formatted || '', assigner: openedTask.assigner?.name || '', executor: openedTask.performers.map((p) => p.name).join(', ') }} onClose={() => setOpenedTask(null)} clientName={openedTask.client?.name} clientId={openedTask.client_id ?? undefined} projectName={openedTask.project?.name} projectId={openedTask.project_id ?? undefined} caseName={openedTask.case?.name} caseId={openedTask.case_id ?? undefined} />}
  </>, document.body);
}
